/**
 * Produtos do pedido (rascunho): produto (código e descrição), unidade, quantidade, preço
 * unitário, desconto, total líquido e ações. Teclado: ↑ ↓ entre as linhas, Enter edita,
 * Delete remove.
 *
 * Totais no rodapé (quando informados): subtotal BRUTO (quantidade × preço), descontos e
 * total LÍQUIDO dos produtos — os mesmos números do resumo da venda, para a conta fechar na
 * tela inteira (ex.: R$ 1,99 − R$ 0,50 = R$ 1,49).
 */
import {
  effectivePrice,
  formatMoney,
  saleItemWarnings,
  saleLineTotal,
  subtractMoney,
  type SaleDraftItem,
} from "@cafegestor/shared";
import { useRef, type KeyboardEvent } from "react";
import { Icon } from "@/components/icons";
import { Badge } from "@/components/ui";
import { qtyText } from "./draft";

const iconButton =
  "inline-flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

export function SaleItemsTable({
  items,
  editingKey,
  flashKey,
  onEdit,
  onRemove,
  totals,
}: {
  items: SaleDraftItem[];
  editingKey: string | null;
  /** linha recém-incluída/alterada: destacada por um instante */
  flashKey: string | null;
  onEdit: (key: string) => void;
  onRemove: (key: string) => void;
  /** subtotal bruto e descontos (os do resumo da venda): mostra o rodapé com os totais */
  totals?: { products: string | null; discounts: string };
}) {
  const rows = useRef<(HTMLTableRowElement | null)[]>([]);
  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
        Nenhum produto incluído. Use a área acima: produto, quantidade e Enter.
      </p>
    );
  }
  function onKeyDown(ev: KeyboardEvent<HTMLTableRowElement>, n: number, key: string) {
    if (ev.target !== ev.currentTarget) return;
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      rows.current[ev.key === "ArrowDown" ? n + 1 : n - 1]?.focus();
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      onEdit(key);
    } else if (ev.key === "Delete") {
      ev.preventDefault();
      onRemove(key);
      rows.current[n + 1]?.focus();
    }
  }
  const net =
    totals && totals.products !== null ? subtractMoney(totals.products, totals.discounts) : null;
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-left text-sm" aria-label="Produtos do pedido">
        <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-3">
              Produto
            </th>
            <th scope="col" className="px-3 py-3">
              Un.
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              Qtd.
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              Preço unit.
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              Desconto
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              Total líquido
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              <span className="sr-only">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {items.map((i, n) => {
            const total = saleLineTotal(i);
            const warnings = saleItemWarnings(i);
            const discount = Number(i.discount) !== 0;
            return (
              <tr
                key={i.key}
                ref={(el) => {
                  rows.current[n] = el;
                }}
                tabIndex={0}
                aria-label={`${i.productCode} ${i.productName}`}
                aria-current={i.key === editingKey ? "true" : undefined}
                className={`align-middle outline-none transition-colors focus-visible:bg-muted ${
                  i.key === flashKey ? "bg-success-soft" : ""
                } ${i.key === editingKey ? "bg-muted" : ""}`}
                onKeyDown={(ev) => onKeyDown(ev, n, i.key)}
              >
                <td className="min-w-48 px-3 py-2.5">
                  <span className="block font-mono text-xs text-muted-foreground">
                    {i.productCode}
                  </span>
                  <span className="font-medium">{i.productName}</span>
                  {i.listPrice === null && (
                    <span className="ml-2">
                      <Badge tone="warn">fora da tabela</Badge>
                    </span>
                  )}
                  {i.listPrice !== null && warnings.length > 0 && (
                    <span className="ml-2">
                      <Badge tone="warn">desconto acima do máximo</Badge>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">{i.unitCode}</td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                  {qtyText(i.quantity)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                  {formatMoney(effectivePrice(i))}
                  {i.unitPrice === null && (
                    <span className="block text-xs text-muted-foreground">da tabela</span>
                  )}
                </td>
                <td
                  className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${
                    discount ? "text-destructive" : "text-muted-foreground"
                  }`}
                >
                  {discount ? `− ${formatMoney(i.discount)}` : "—"}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
                  {formatMoney(total)}
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    className={iconButton}
                    aria-label={`Editar ${i.productCode}`}
                    title={`Editar ${i.productCode}`}
                    onClick={() => onEdit(i.key)}
                  >
                    <Icon name="edit" size={17} />
                  </button>
                  <button
                    type="button"
                    className={`${iconButton} hover:bg-destructive-soft hover:text-destructive`}
                    aria-label={`Remover ${i.productCode}`}
                    title={`Remover ${i.productCode}`}
                    onClick={() => onRemove(i.key)}
                  >
                    <Icon name="trash" size={17} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        {totals && (
          <tfoot className="border-t border-border/70 bg-background/60 text-sm tabular-nums">
            <tr>
              <th
                scope="row"
                colSpan={5}
                className="px-3 pt-3 pb-1 text-right font-normal text-muted-foreground"
              >
                Subtotal bruto
              </th>
              <td className="px-3 pt-3 pb-1 text-right whitespace-nowrap">
                {totals.products === null ? "—" : formatMoney(totals.products)}
              </td>
              <td />
            </tr>
            <tr>
              <th
                scope="row"
                colSpan={5}
                className="px-3 py-1 text-right font-normal text-muted-foreground"
              >
                Descontos
              </th>
              <td className="px-3 py-1 text-right whitespace-nowrap text-destructive">
                {Number(totals.discounts) === 0 ? "—" : `− ${formatMoney(totals.discounts)}`}
              </td>
              <td />
            </tr>
            <tr>
              <th scope="row" colSpan={5} className="px-3 pt-1 pb-3 text-right font-semibold">
                Total dos produtos
              </th>
              <td
                className="px-3 pt-1 pb-3 text-right font-bold whitespace-nowrap"
                aria-live="polite"
              >
                {net === null ? "—" : formatMoney(net)}
              </td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
